export const AUTH_ENTRY_BRIDGE_KEY = "__SIMBEL_AUTH_ENTRY_URL__";
export const AUTH_TELEMETRY_REDACTED = "[REDACTED]";

interface IAuthEntryBridge {
  originalUrl: string;
  scrubbedUrl: string;
}

interface IAuthPrivacyBrowser {
  location: Pick<Location, "href">;
  history: Pick<History, "state" | "replaceState">;
}

const AUTH_SCREEN_PATH = /^(?:\/$|\/(?:auth|oauth|sso|login|register|reset-password|forgot-password)(?:\/|$))/i;
const AUTH_ENDPOINT_PATH = /\/(?:auth|oauth|oidc|sso)(?:\/|$)/i;

function normalizedCredentialKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function isCredentialKey(key: string): boolean {
  const normalized = normalizedCredentialKey(key);
  return (
    normalized === "authorization" ||
    normalized === "cookie" ||
    normalized === "cookies" ||
    normalized === "setcookie" ||
    normalized === "password" ||
    normalized.endsWith("password") ||
    normalized === "digest" ||
    normalized.endsWith("digest") ||
    normalized === "code" ||
    normalized === "authcode" ||
    normalized === "state" ||
    normalized === "verifier" ||
    normalized.endsWith("verifier") ||
    normalized === "ticket" ||
    normalized.endsWith("ticket") ||
    normalized === "proof" ||
    normalized.endsWith("proof") ||
    normalized === "csrf" ||
    normalized.endsWith("token")
  );
}

function hasSensitiveUrlMaterial(url: URL): boolean {
  if (url.username || url.password) return true;
  for (const key of url.searchParams.keys()) {
    if (isCredentialKey(key)) return true;
  }
  const fragment = new URLSearchParams(url.hash.slice(1));
  for (const key of fragment.keys()) {
    if (isCredentialKey(key)) return true;
  }
  return false;
}

function scrubUrl(url: URL): void {
  url.username = "";
  url.password = "";
  if (url.pathname === "/oauth/auto-login") {
    for (const key of [...url.searchParams.keys()]) {
      if (!["client_id", "next_url", "embed", "result"].includes(key)) url.searchParams.delete(key);
    }
    url.hash = "";
  }
  for (const key of [...url.searchParams.keys()]) {
    if (isCredentialKey(key)) url.searchParams.delete(key);
  }

  const fragment = new URLSearchParams(url.hash.slice(1));
  let fragmentChanged = false;
  for (const key of [...fragment.keys()]) {
    if (isCredentialKey(key)) {
      fragment.delete(key);
      fragmentChanged = true;
    }
  }
  if (fragmentChanged) url.hash = fragment.size ? fragment.toString() : "";
}

function isAbsoluteUrl(value: string): boolean {
  return /^[a-z][a-z\d+.-]*:\/\//i.test(value);
}

export function sanitizeAuthUrl(value: string): string {
  try {
    const absolute = isAbsoluteUrl(value);
    const url = new URL(value, "http://auth-privacy.invalid");
    scrubUrl(url);
    return absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return sanitizeTelemetryText(value);
  }
}

function sanitizeTelemetryText(value: string): string {
  let sanitized = value.replace(/\b(?:Bearer|Basic)\s+[^\s,;]+/gi, `Bearer ${AUTH_TELEMETRY_REDACTED}`);
  sanitized = sanitized.replace(
    /((?:authorization|set-cookie|cookie|password|digest|access[_-]?token|refresh[_-]?token|id[_-]?token|sso[_-]?token|reset[_-]?token|auth[_-]?code|code[_-]?verifier|verifier|code|state|ticket|proof)\s*[=:]\s*)(["']?)[^\s&#;,"']+\2/gi,
    `$1${AUTH_TELEMETRY_REDACTED}`,
  );
  return sanitized.replace(/https?:\/\/[^\s"'<>]+/gi, (url) => sanitizeAuthUrl(url));
}

function cloneSanitized(value: unknown, seen: WeakMap<object, unknown>): unknown {
  if (typeof value === "string") return sanitizeTelemetryText(value);
  if (value === null || typeof value !== "object") return value;
  if (seen.has(value)) return seen.get(value);
  if (value instanceof Date || value instanceof RegExp) return value;
  if (value instanceof Error) {
    const result = {
      name: value.name,
      message: sanitizeTelemetryText(value.message),
      stack: value.stack ? sanitizeTelemetryText(value.stack) : undefined,
    };
    seen.set(value, result);
    return result;
  }
  if (Array.isArray(value)) {
    const result: unknown[] = [];
    seen.set(value, result);
    value.forEach((item) => result.push(cloneSanitized(item, seen)));
    return result;
  }

  const result: Record<string, unknown> = {};
  seen.set(value, result);
  Object.entries(value).forEach(([key, item]) => {
    result[key] = isCredentialKey(key) ? AUTH_TELEMETRY_REDACTED : cloneSanitized(item, seen);
  });
  return result;
}

export function sanitizeAuthTelemetry<T>(value: T): T {
  return cloneSanitized(value, new WeakMap()) as T;
}

function browserTarget(): IAuthPrivacyBrowser | undefined {
  return typeof window === "undefined" ? undefined : window;
}

function readBridge(target: IAuthPrivacyBrowser): IAuthEntryBridge | undefined {
  const candidate = Reflect.get(target, AUTH_ENTRY_BRIDGE_KEY) as Partial<IAuthEntryBridge> | undefined;
  if (typeof candidate?.originalUrl !== "string" || typeof candidate.scrubbedUrl !== "string") return undefined;
  return { originalUrl: candidate.originalUrl, scrubbedUrl: candidate.scrubbedUrl };
}

export function captureAndScrubAuthEntryUrl(target: IAuthPrivacyBrowser | undefined = browserTarget()): void {
  if (!target) return;
  const originalUrl = target.location.href;
  const scrubbedUrl = sanitizeAuthUrl(originalUrl);
  if (scrubbedUrl === originalUrl) return;

  Reflect.defineProperty(target, AUTH_ENTRY_BRIDGE_KEY, {
    configurable: true,
    enumerable: false,
    writable: true,
    value: { originalUrl, scrubbedUrl } satisfies IAuthEntryBridge,
  });
  try {
    target.history.replaceState(target.history.state, "", scrubbedUrl);
  } catch (error) {
    Reflect.deleteProperty(target, AUTH_ENTRY_BRIDGE_KEY);
    throw error;
  }
}

export function getAuthEntryUrl(target: IAuthPrivacyBrowser | undefined = browserTarget()): string {
  if (!target) return "";
  const currentUrl = target.location.href;
  const entry = readBridge(target);
  Reflect.deleteProperty(target, AUTH_ENTRY_BRIDGE_KEY);
  if (!entry) return currentUrl;
  try {
    const captured = new URL(entry.scrubbedUrl);
    const current = new URL(currentUrl);
    // Vue Router may re-encode a retained query value (notably `iss`) during startup.
    const sameLocation =
      captured.origin === current.origin &&
      captured.pathname === current.pathname &&
      captured.searchParams.toString() === current.searchParams.toString() &&
      captured.hash === current.hash;
    return sameLocation ? entry.originalUrl : currentUrl;
  } catch {
    return currentUrl;
  }
}

export function isAuthTelemetrySuppressed(value?: string | URL): boolean {
  const rawUrl = value?.toString() ?? browserTarget()?.location.href;
  if (!rawUrl) return true;
  try {
    const url = new URL(rawUrl, "http://auth-privacy.invalid");
    return AUTH_SCREEN_PATH.test(url.pathname) || hasSensitiveUrlMaterial(url);
  } catch {
    return true;
  }
}

export function isAuthTelemetryEventSuppressed(event: unknown): boolean {
  if (!event || typeof event !== "object") return false;
  const candidate = event as { request?: { url?: unknown }; transaction?: unknown };
  const values = [candidate.request?.url, candidate.transaction];
  return values.some((value) => {
    if (typeof value !== "string") return false;
    try {
      const url = new URL(value, "http://auth-privacy.invalid");
      return AUTH_ENDPOINT_PATH.test(url.pathname) || hasSensitiveUrlMaterial(url);
    } catch {
      return true;
    }
  });
}
